import { create } from "zustand";
import { applyBrandPack, type BrandPackId } from "@/theme/brandPacks";
import type { LocaleCode } from "@/i18n/messages";
import { translate, type MessageKey, type TranslateParams } from "@/i18n";

type UiState = {
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  darkMode: boolean;
  locale: LocaleCode;
  brandPack: BrandPackId;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  setDarkMode: (on: boolean) => void;
  setLocale: (locale: LocaleCode) => void;
  setBrandPack: (pack: BrandPackId) => void;
  t: (key: MessageKey | string, params?: TranslateParams) => string;
};

function applyDarkClass(on: boolean) {
  document.documentElement.classList.toggle("dark", on);
}

function applyLocale(locale: LocaleCode) {
  document.documentElement.lang = locale;
}

/** v2 key: reset leftover dark preference from before class-based theme fix. Default = light. */
const DARK_MODE_KEY = "amos_dark_mode_v2";
const SIDEBAR_COLLAPSED_KEY = "amos_sidebar_collapsed";
const legacyDark = localStorage.getItem("amos_dark_mode");
if (legacyDark !== null) localStorage.removeItem("amos_dark_mode");

const savedDark = localStorage.getItem(DARK_MODE_KEY) === "1";
const savedSidebarCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
const savedLocale = (localStorage.getItem("amos_locale") || "en") as LocaleCode;
const savedBrandRaw = localStorage.getItem("amos_brand_pack");
const savedBrand = (savedBrandRaw && ["classic", "slate", "teal"].includes(savedBrandRaw) ? savedBrandRaw : null) as BrandPackId | null;

applyDarkClass(savedDark);
applyLocale(["en", "es", "hi"].includes(savedLocale) ? savedLocale : "en");
if (savedBrand) {
  applyBrandPack(savedBrand);
}

export const useUiStore = create<UiState>((set, get) => ({
  sidebarCollapsed: savedSidebarCollapsed,
  mobileSidebarOpen: false,
  darkMode: savedDark,
  locale: ["en", "es", "hi"].includes(savedLocale) ? savedLocale : "en",
  brandPack: savedBrand || "classic",
  toggleSidebar: () =>
    set((s) => {
      const next = !s.sidebarCollapsed;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {}
      return { sidebarCollapsed: next };
    }),
  setSidebarCollapsed: (collapsed) => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {}
    set({ sidebarCollapsed: collapsed });
  },
  setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
  toggleMobileSidebar: () => set((s) => ({ mobileSidebarOpen: !s.mobileSidebarOpen })),
  closeMobileSidebar: () => set({ mobileSidebarOpen: false }),
  setDarkMode: (on) => {
    localStorage.setItem(DARK_MODE_KEY, on ? "1" : "0");
    applyDarkClass(on);
    set({ darkMode: on });
  },
  setLocale: (locale) => {
    localStorage.setItem("amos_locale", locale);
    applyLocale(locale);
    set({ locale });
  },
  setBrandPack: (pack) => {
    localStorage.setItem("amos_brand_pack", pack);
    applyBrandPack(pack);
    set({ brandPack: pack });
  },
  t: (key, params) => translate(get().locale, key, params),
}));
